const preloadedStickerImages = new Map();
const sharedStickerPositions = {
  desktop: [
    { x: 0.13, y: 0, sourceIndex: 0, sizeRatio: 0.3 },
    { x: 0.965, y: 0.175, sourceIndex: 0, sizeRatio: 1 },
    { x: 0.083, y: 0.58, sourceIndex: 1, sizeRatio: 1 },
    { x: 0.897, y: 0.6, sourceIndex: 3, sizeRatio: 0.9 },
    { x: 0.19, y: 0.89, sourceIndex: 4, sizeRatio: 0.9 },
    { x: 0.87, y: 0.94, sourceIndex: 5, sizeRatio: 1 },
  ],
  mobile: [],
};

class CHMemoriesStickerLayer {
  /** @param {HTMLElement} root */
  constructor(root) {
    this.root = root;
    this.overlay = root.querySelector('[ref="stickers"]');
    this.cursor = root.querySelector('[ref="stickerCursor"]');
    this.sourceNode = root.querySelector('[ref="stickerSources"]');
    this.controller = new AbortController();
    this.pendingStamp = null;
    this.maxStickers = 10;
    this.maxAmbientStickers = Math.max(
      sharedStickerPositions.desktop.length,
      sharedStickerPositions.mobile.length
    );
    this.ambientTimer = null;
    this.ambientObserver = null;
    this.ambientResizeObserver = null;
    this.ambientLayoutFrame = null;
    this.ambientStickers = [];
    this.ambientHasPlayed = false;
    this.clickIsActive = root.dataset.enableStickers === "true";
    this.ambientIsActive =
      root.dataset.cycleStickers === "true" &&
      this.maxAmbientStickers > 0;
    this.previewIsAvailable =
      root.dataset.showStickerCursor === "true" &&
      window.matchMedia("(pointer: fine)").matches;
    this.currentStickerUrl = "";
    this.currentStickerIndex = 0;

    this.stickerSources = this.#parseStickerSources();
    this.isActive = Boolean(
      (this.clickIsActive || this.ambientIsActive) &&
        this.overlay &&
        this.sourceNode &&
        this.stickerSources.length
    );

    if (!this.isActive) {
      this.#hidePreview();
      return;
    }

    if (this.clickIsActive) this.#buildCursor();
    this.#preloadStickerImages();
    if (this.clickIsActive) this.#bindEvents();
    if (this.ambientIsActive) this.#setupAmbientStickers();
  }

  destroy() {
    this.controller.abort();
    window.clearTimeout(this.ambientTimer);
    this.ambientObserver?.disconnect();
    this.ambientResizeObserver?.disconnect();
    window.cancelAnimationFrame(this.ambientLayoutFrame);
    this.#hidePreview();
    this.root.removeAttribute("sticker-preview-visible");
  }

  #setupAmbientStickers() {
    window.addEventListener("resize", this.#queueAmbientLayout, {
      signal: this.controller.signal,
      passive: true,
    });

    if ("ResizeObserver" in window) {
      this.ambientResizeObserver = new ResizeObserver(this.#queueAmbientLayout);
      this.ambientResizeObserver.observe(this.root);
    }

    if (!("IntersectionObserver" in window)) {
      this.ambientHasPlayed = true;
      this.#scheduleAmbientSticker(280, 0);
      return;
    }

    this.ambientObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting || this.ambientHasPlayed) return;

        this.ambientHasPlayed = true;
        this.ambientObserver?.disconnect();
        this.#scheduleAmbientSticker(280, 0);
      },
      { threshold: 0.22 }
    );
    this.ambientObserver.observe(this.root);
  }

  #scheduleAmbientSticker(delay, index) {
    if (index >= this.#activeStickerPositions().length) return;

    window.clearTimeout(this.ambientTimer);
    this.ambientTimer = window.setTimeout(() => {
      if (document.hidden) {
        this.#scheduleAmbientSticker(300, index);
        return;
      }

      this.#placeAmbientSticker(index);

      if (index + 1 < this.#activeStickerPositions().length) {
        this.#scheduleAmbientSticker(this.#ambientEntranceSpacing(), index + 1);
      }
    }, delay);
  }

  #ambientEntranceSpacing() {
    const pace = Number.parseFloat(this.root.dataset.stickerCycleInterval) || 3;

    return Math.min(Math.max(180, pace * 70 + 70), 560);
  }

  #placeAmbientSticker(index) {
    if (!this.overlay) return;

    const position = this.#activeStickerPositions()[index];
    if (!position) return;

    const sizeRatio = position.sizeRatio;
    const size = this.#ambientStickerSize(sizeRatio);
    const point = this.#findPhotoSafePoint(size, index);
    if (!point) return;

    const sticker = document.createElement("div");
    sticker.className = "ch-click-sticker ch-click-sticker--ambient";
    const image = document.createElement("img");
    image.alt = "";
    image.decoding = "async";
    image.draggable = false;
    image.src = this.stickerSources[position.sourceIndex % this.stickerSources.length];

    sticker.style.setProperty("--sticker-x", `${point.x.toFixed(2)}px`);
    sticker.style.setProperty("--sticker-y", `${point.y.toFixed(2)}px`);
    sticker.style.setProperty("--sticker-size", `${size.toFixed(2)}px`);
    sticker.dataset.ambientStickerIndex = String(index);
    sticker.dataset.ambientSizeRatio = sizeRatio.toFixed(4);
    const stickerScale = 1;

    sticker.style.setProperty("--sticker-scale", stickerScale.toFixed(3));
    sticker.style.setProperty(
      "--ambient-pop-scale",
      (stickerScale * 1.08).toFixed(3)
    );
    sticker.style.setProperty(
      "--ambient-rebound-scale",
      (stickerScale * 0.96).toFixed(3)
    );
    sticker.style.setProperty(
      "--ambient-soft-scale",
      (stickerScale * 1.015).toFixed(3)
    );
    const rootBounds = this.root.getBoundingClientRect();
    const horizontalDirection = point.x < rootBounds.width / 2 ? -1 : 1;
    const verticalDirection = point.y < rootBounds.height * 0.52 ? -1 : 1;
    const enterX = horizontalDirection * this.#randomBetween(52, 96);
    const enterY = verticalDirection * this.#randomBetween(34, 72);

    sticker.style.setProperty("--ambient-enter-x", `${enterX.toFixed(2)}px`);
    sticker.style.setProperty("--ambient-enter-y", `${enterY.toFixed(2)}px`);
    sticker.style.setProperty(
      "--ambient-overshoot-x",
      `${(-enterX * 0.09).toFixed(2)}px`
    );
    sticker.style.setProperty(
      "--ambient-overshoot-y",
      `${(-enterY * 0.09).toFixed(2)}px`
    );
    sticker.style.setProperty(
      "--ambient-rebound-x",
      `${(enterX * 0.032).toFixed(2)}px`
    );
    sticker.style.setProperty(
      "--ambient-rebound-y",
      `${(enterY * 0.032).toFixed(2)}px`
    );
    sticker.style.setProperty(
      "--ambient-soft-x",
      `${(-enterX * 0.011).toFixed(2)}px`
    );
    sticker.style.setProperty(
      "--ambient-soft-y",
      `${(-enterY * 0.011).toFixed(2)}px`
    );
    sticker.style.setProperty(
      "--ambient-enter-rotation",
      `${(index % 2 === 0 ? 14 : -14).toFixed(2)}deg`
    );
    sticker.style.setProperty(
      "--ambient-enter-duration",
      `${this.#randomBetween(880, 1060).toFixed(0)}ms`
    );
    sticker.append(image);
    this.overlay.append(sticker);
    this.ambientStickers.push(sticker);
    requestAnimationFrame(() => sticker.classList.add("is-visible"));
  }

  #queueAmbientLayout = () => {
    window.cancelAnimationFrame(this.ambientLayoutFrame);
    this.ambientLayoutFrame = window.requestAnimationFrame(() => {
      this.ambientLayoutFrame = null;
      this.#layoutAmbientStickers();
      if (!this.ambientHasPlayed) return;

      this.#activeStickerPositions().forEach((_, index) => {
        const exists = this.ambientStickers.some(
          (sticker) => sticker.dataset.ambientStickerIndex === String(index)
        );
        if (!exists) this.#placeAmbientSticker(index);
      });
    });
  };

  #layoutAmbientStickers() {
    this.ambientStickers = this.ambientStickers.filter((sticker) => {
      if (!sticker.isConnected) return false;

      const index = Number.parseInt(sticker.dataset.ambientStickerIndex, 10);
      const position = this.#activeStickerPositions()[index];
      if (!position) {
        sticker.remove();
        return false;
      }

      const size = this.#ambientStickerSize(position.sizeRatio);
      const point = this.#findPhotoSafePoint(size, index);

      if (!point) {
        sticker.remove();
        return false;
      }

      sticker.style.setProperty("--sticker-x", `${point.x.toFixed(2)}px`);
      sticker.style.setProperty("--sticker-y", `${point.y.toFixed(2)}px`);
      sticker.style.setProperty("--sticker-size", `${size.toFixed(2)}px`);
      sticker.dataset.ambientSizeRatio = position.sizeRatio.toFixed(4);
      const image = sticker.querySelector("img");
      if (image) {
        image.src = this.stickerSources[position.sourceIndex % this.stickerSources.length];
      }
      return true;
    });
  }

  #ambientStickerSize(sizeRatio) {
    const { low, high } = this.#stickerSizeRange();
    const normalizedRatio = Number.isFinite(sizeRatio) ? sizeRatio : 0.5;

    return low + (high - low) * normalizedRatio;
  }

  #findPhotoSafePoint(size, index) {
    const rootBounds = this.root.getBoundingClientRect();
    if (rootBounds.width <= size || rootBounds.height <= size) return null;

    const position = this.#activeStickerPositions()[index];
    if (!position) return null;

    return {
      x: rootBounds.width * position.x,
      y: rootBounds.height * position.y,
    };
  }

  #activeStickerPositions() {
    return window.matchMedia("(max-width: 749px)").matches
      ? sharedStickerPositions.mobile
      : sharedStickerPositions.desktop;
  }

  #bindEvents() {
    const { signal } = this.controller;

    this.root.addEventListener("pointerenter", this.#handlePointerMove, {
      signal,
    });
    this.root.addEventListener("pointermove", this.#handlePointerMove, {
      signal,
      passive: true,
    });
    this.root.addEventListener("pointerleave", this.#handlePointerLeave, {
      signal,
    });
    this.root.addEventListener("pointerdown", this.#handlePointerDown, {
      signal,
      capture: true,
    });
    this.root.addEventListener("pointerup", this.#handlePointerUp, {
      signal,
      capture: true,
    });
    this.root.addEventListener("pointercancel", this.#handlePointerCancel, {
      signal,
      capture: true,
    });
  }

  #buildCursor() {
    if (!this.previewIsAvailable || !this.cursor) return;

    this.cursorImage = document.createElement("img");
    this.cursorImage.alt = "";
    this.cursorImage.draggable = false;
    this.cursor.append(this.cursorImage);
    this.#refreshCursorSticker();
  }

  #parseStickerSources() {
    if (!this.sourceNode?.textContent) return [];

    try {
      const parsed = JSON.parse(this.sourceNode.textContent);
      return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
    } catch (error) {
      console.warn("CH memories stickers: invalid sticker source JSON", error);
      return [];
    }
  }

  #pickStickerUrl() {
    if (!this.stickerSources.length) return "";
    return this.stickerSources[this.currentStickerIndex] || "";
  }

  #advanceStickerCursor() {
    if (!this.stickerSources.length) return;

    this.currentStickerIndex =
      (this.currentStickerIndex + 1) % this.stickerSources.length;
  }

  #refreshCursorSticker() {
    this.currentStickerUrl = this.#pickStickerUrl();

    if (!this.cursorImage || !this.currentStickerUrl) return;
    this.cursorImage.src = this.currentStickerUrl;
  }

  #preloadStickerImages() {
    this.stickerSources.forEach((src) => {
      if (!src || preloadedStickerImages.has(src)) return;

      const image = new Image();
      image.decoding = "async";
      image.loading = "eager";
      image.src = src;

      const ready = image
        .decode?.()
        .catch(() => undefined);

      preloadedStickerImages.set(src, { image, ready });
    });
  }

  /** @param {PointerEvent} event */
  #handlePointerDown = (event) => {
    if (!this.#isPrimaryPointer(event)) return;

    const target = event.target instanceof Element ? event.target : null;
    this.pendingStamp = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      moved: false,
      canStamp: this.#canStampTarget(target),
    };

    if (!this.pendingStamp.canStamp) {
      this.#hidePreview();
    }
  };

  /** @param {PointerEvent} event */
  #handlePointerMove = (event) => {
    if (this.pendingStamp && this.pendingStamp.pointerId === event.pointerId) {
      const distance = Math.hypot(
        event.clientX - this.pendingStamp.startX,
        event.clientY - this.pendingStamp.startY
      );

      if (distance > 10) {
        this.pendingStamp.moved = true;
      }
    }

    this.#updatePreview(event);
  };

  /** @param {PointerEvent} event */
  #handlePointerUp = (event) => {
    if (
      !this.pendingStamp ||
      this.pendingStamp.pointerId !== event.pointerId ||
      !this.#isPrimaryPointer(event)
    ) {
      return;
    }

    const target = event.target instanceof Element ? event.target : null;
    const canStamp =
      this.pendingStamp.canStamp &&
      !this.pendingStamp.moved &&
      this.#canStampTarget(target) &&
      this.#pointIsInsideRoot(event.clientX, event.clientY);

    if (canStamp) {
      this.#placeSticker(event.clientX, event.clientY);
    }

    this.pendingStamp = null;
    this.#updatePreview(event);
  };

  #handlePointerCancel = () => {
    this.pendingStamp = null;
    this.#hidePreview();
  };

  #handlePointerLeave = () => {
    this.pendingStamp = null;
    this.#hidePreview();
  };

  /** @param {PointerEvent} event */
  #updatePreview(event) {
    if (
      !this.previewIsAvailable ||
      !this.cursor ||
      !this.cursorImage ||
      this.root.hasAttribute("dragging")
    ) {
      this.#hidePreview();
      return;
    }

    const target = event.target instanceof Element ? event.target : null;
    if (!this.#canStampTarget(target)) {
      this.#hidePreview();
      return;
    }

    if (!this.currentStickerUrl) {
      this.#refreshCursorSticker();
    }

    const { x, y } = this.#toLocalPoint(event.clientX, event.clientY);
    this.cursor.style.setProperty("--cursor-x", `${x}px`);
    this.cursor.style.setProperty("--cursor-y", `${y}px`);
    this.cursor.hidden = false;
    this.root.setAttribute("sticker-preview-visible", "");
  }

  #hidePreview() {
    if (this.cursor) {
      this.cursor.hidden = true;
    }

    this.root.removeAttribute("sticker-preview-visible");
  }

  /** @param {number} clientX @param {number} clientY */
  #placeSticker(clientX, clientY) {
    if (!this.overlay) return;

    const sticker = document.createElement("div");
    sticker.className = "ch-click-sticker";

    const image = document.createElement("img");
    image.alt = "";
    image.decoding = "async";
    image.loading = "eager";
    image.draggable = false;
    image.src = this.currentStickerUrl || this.#pickStickerUrl();

    const { x, y } = this.#toLocalPoint(clientX, clientY);
    const size = this.#randomSize();
    const bounds = this.root.getBoundingClientRect();

    sticker.style.setProperty("--sticker-x", `${x.toFixed(2)}px`);
    sticker.style.setProperty("--sticker-y", `${y.toFixed(2)}px`);
    sticker.style.setProperty("--sticker-size", `${size.toFixed(2)}px`);
    sticker.dataset.chStickerViewport = window.innerWidth < 750 ? "mobile" : "desktop";
    sticker.dataset.chStickerX = ((x / bounds.width) * 100).toFixed(2);
    sticker.dataset.chStickerY = ((y / bounds.height) * 100).toFixed(2);
    sticker.dataset.chStickerSize = size.toFixed(2);
    sticker.append(image);
    this.overlay.append(sticker);

    const clickStickers = this.overlay.querySelectorAll(
      ".ch-click-sticker:not(.ch-click-sticker--ambient)"
    );
    if (clickStickers.length > this.maxStickers) {
      clickStickers[0]?.remove();
    }

    requestAnimationFrame(() => {
      sticker.classList.add("is-visible");
    });

    this.#scheduleStickerRemoval(sticker);

    this.#advanceStickerCursor();
    this.#refreshCursorSticker();
  }

  /** @param {HTMLElement} sticker */
  #scheduleStickerRemoval(sticker) {
    const styles = getComputedStyle(this.root);
    const fadeAfterSeconds =
      parseFloat(styles.getPropertyValue("--ch-click-sticker-fade-after")) || 0;
    const fadeDurationMs =
      parseFloat(styles.getPropertyValue("--ch-click-sticker-fade-duration")) ||
      420;

    if (fadeAfterSeconds <= 0) return;

    window.setTimeout(() => {
      sticker.classList.add("is-fading");

      window.setTimeout(() => {
        sticker.remove();
      }, fadeDurationMs);
    }, fadeAfterSeconds * 1000);
  }

  #randomSize() {
    const { low, high } = this.#stickerSizeRange();

    return this.#randomBetween(low, high);
  }

  #stickerSizeRange() {
    const styles = getComputedStyle(this.root);
    const mobileMinSize = parseFloat(
      styles.getPropertyValue("--ch-click-sticker-mobile-min-size")
    );
    const mobileMaxSize = parseFloat(
      styles.getPropertyValue("--ch-click-sticker-mobile-max-size")
    );
    const desktopMinSize =
      parseFloat(styles.getPropertyValue("--ch-click-sticker-min-size")) ||
      parseFloat(styles.getPropertyValue("--memories-sticker-min-size")) ||
      112;
    const desktopMaxSize =
      parseFloat(styles.getPropertyValue("--ch-click-sticker-max-size")) ||
      parseFloat(styles.getPropertyValue("--memories-sticker-max-size")) ||
      168;
    const progress = Math.min(Math.max((window.innerWidth - 360) / 840, 0), 1);
    const minSize =
      (mobileMinSize || desktopMinSize) * (1 - progress) +
      desktopMinSize * progress;
    const maxSize =
      (mobileMaxSize || desktopMaxSize) * (1 - progress) +
      desktopMaxSize * progress;

    return {
      low: Math.min(minSize, maxSize),
      high: Math.max(minSize, maxSize),
      progress,
    };
  }

  /** @param {number} min @param {number} max */
  #randomBetween(min, max) {
    return min + Math.random() * (max - min);
  }

  /** @param {PointerEvent} event */
  #isPrimaryPointer(event) {
    return event.isPrimary && event.button === 0;
  }

  /** @param {Element | null} target */
  #canStampTarget(target) {
    if (!target) return false;

    return !target.closest(
      'a, button, input, select, textarea, summary, label, [on\\:click]'
    );
  }

  /** @param {number} clientX @param {number} clientY */
  #pointIsInsideRoot(clientX, clientY) {
    const bounds = this.root.getBoundingClientRect();
    return (
      clientX >= bounds.left &&
      clientX <= bounds.right &&
      clientY >= bounds.top &&
      clientY <= bounds.bottom
    );
  }

  /** @param {number} clientX @param {number} clientY */
  #toLocalPoint(clientX, clientY) {
    const bounds = this.root.getBoundingClientRect();
    return {
      x: Math.min(Math.max(clientX - bounds.left, 0), bounds.width),
      y: Math.min(Math.max(clientY - bounds.top, 0), bounds.height),
    };
  }
}

const instances = new WeakMap();

/** @param {ParentNode} scope */
function initMemoriesStickerLayers(scope = document) {
  if (!scope || typeof scope.querySelectorAll !== "function") return;

  scope
    .querySelectorAll("[data-sticker-interaction='true']")
    .forEach((element) => {
      if (!(element instanceof HTMLElement) || instances.has(element)) return;

      const instance = new CHMemoriesStickerLayer(element);
      if (!instance.isActive) return;

      instances.set(element, instance);
    });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    initMemoriesStickerLayers();
  });
} else {
  initMemoriesStickerLayers();
}

document.addEventListener("shopify:section:load", (event) => {
  if (!(event.target instanceof HTMLElement)) return;
  initMemoriesStickerLayers(event.target);
});

document.addEventListener("shopify:section:unload", (event) => {
  if (!(event.target instanceof HTMLElement)) return;

  event.target
    .querySelectorAll("[data-sticker-interaction='true']")
    .forEach((element) => {
      if (!(element instanceof HTMLElement)) return;
      const instance = instances.get(element);
      instance?.destroy();
      instances.delete(element);
    });
});
